import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { 
  useChat
} from '@livekit/components-react';
import { MoreVertical, Check, X, RotateCcw, RefreshCw, Trash, MoreHorizontal, Edit3, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import GalleryPopup from './GalleryPopup';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { ChatControlWrapper, PrestartChatControl, LiveChatControl } from '../../shared/ChatControls';
import { HintPanel } from '../../shared/HintPanel';
import '@/app/ui/scrollbar.css';
import type { ChatMessage } from '@/app/lib/data';
import { formatMessageContent } from '../../shared/textUtil';
import { LiveMessage} from '../../../data/liveMessageData/liveMessage';
import { useLiveMessages, useLiveMessageActions, useLiveMessagesWithImageUpdates } from '../../../hooks/useLiveMessages';
import { useCharacterData, useChatStatus, useGlobalChatState, useGlobalLiveKitRoomState } from '../../../hooks/useGlobalChatState';
import { useDataReceived, useRemoveMessageFromChat, useEditMessage, useUpdateActiveImage } from '../../../hooks/useDataReceived';
import { useStickyScroll } from '../../../hooks/useStickyScroll';
import { useStickyScrollCallback } from '../../../hooks/useStickyScrollCallback';
import { getPresignedUrl } from '@/app/lib/actions/s3';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { ChatStatus } from '../../../data/globalChatState';
import { EditMessagePopup } from '../../shared/EditMessagePopup';
import { BacktrackConfirmPopup } from '../../shared/Popups';
import { AdvancedImage } from '@cloudinary/react';
import ImagePreviewPopup from '../../shared/ImagePreviewPopup';
import { useInChatImageGen } from '../../../hooks/useImageGenApi';
import { useGeminiLive } from '../../../hooks/useGeminiLive';
import ChatAudioPulse from '../../shared/ChatAudioPulse';


// Portal Dropdown Component
interface PortalDropdownProps {
  isOpen: boolean;
  position: { top: number; left: number; right: number; bottom: number };
  isMsgFromUser: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

function PortalDropdown({ isOpen, position, isMsgFromUser, onClose, children }: PortalDropdownProps) {
  if (!isOpen || typeof window === 'undefined') return null;

  const dropdownStyle: React.CSSProperties = {
    position: 'fixed',
    bottom: window.innerHeight - position.top + 15, // Position above button with 6px gap
    left: isMsgFromUser ? 'auto' : position.left - 60, // -left-10 equivalent
    right: isMsgFromUser ? window.innerWidth - position.right : 'auto', // -right-10 equivalent
    zIndex: 9999,
  };

  return createPortal(
    <>
      {/* Dark overlay */}
      <div 
        className="fixed inset-0 bg-black/20 z-[9998] pointer-events-none"
        onClick={onClose}
      />
      {/* Dropdown */}
      <div 
        className="bg-[#343434] rounded-xl shadow-2xl w-auto"
        style={dropdownStyle}
        data-message-menu
      >
        {children}
      </div>
    </>,
    document.body
  );
}

interface TextChatProps {
  onStartChat?: () => void; // Callback for when user wants to start chat
  showResizeHandle?: boolean; // Control visibility of the resize handle
  isGalleryOpen?: boolean; // Gallery popup state
  onToggleGallery?: () => void; // Gallery toggle callback
  onImageClick?: (message: LiveMessage) => void; // Callback for when user clicks on an image
}

export const TextChat = React.memo(function TextChat({
  onStartChat,
  showResizeHandle = false,
  isGalleryOpen = false,
  onToggleGallery,
  onImageClick,
}: TextChatProps) {
  const t = useTranslations('chat');

  // Use hooks instead of local state and callbacks - WITH IMAGE UPDATES for re-rendering
  const { messages } = useLiveMessagesWithImageUpdates();
  const { character } = useCharacterData();
  const [globalChatState, globalChatStateActions] = useGlobalChatState();
  const { chatStatus } = useChatStatus();
  
  // Get room from global state instead of context
  const { globalLKRoomState } = useGlobalLiveKitRoomState();
  const room = globalLKRoomState.room;

  const avatar_name = character?.metadata.avatar_name || 'Unknown Avatar';
  const avatarId = character?.metadata.avatar_id || '';

  // Session hook for credit checking
  const { session, userEmail, isAuthenticated, isLoading: authLoading } = useAuth();
  
  // User preferred name state
  const [userPreferredName, setUserPreferredName] = useState<string>('');
  
  // Local UI state - not message data
  const [showMenu, setShowMenu] = useState(false);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [needsCollapse, setNeedsCollapse] = useState(false);
  const bioTextRef = useRef<HTMLParagraphElement>(null);
  const [showMessages, setShowMessages] = useState(true);

  // Check if bio text needs collapsing
  useEffect(() => {
    if (bioTextRef.current && character?.metadata.agent_bio) {
      const element = bioTextRef.current;
      // Temporarily remove line-clamp to measure full height
      const originalStyle = element.style.cssText;
      element.style.cssText = 'display: block; -webkit-line-clamp: none; -webkit-box-orient: vertical; overflow: visible;';
      const fullHeight = element.scrollHeight;
      
      // Restore original style
      element.style.cssText = originalStyle;
      
      // Check if text would exceed 3 lines (approximately 4.5rem with line-height 1.5)
      const lineHeight = parseFloat(getComputedStyle(element).lineHeight) || 21; // Default line height
      const maxHeight = lineHeight * 3;
      
      setNeedsCollapse(fullHeight > maxHeight);
    }
  }, [character?.metadata.agent_bio]);
  
  // Message menu state for dropdown
  const [messageMenuState, setMessageMenuState] = useState<{ [key: string]: boolean }>({});
  const [buttonPositions, setButtonPositions] = useState<{ [key: string]: { top: number; left: number; right: number; bottom: number } }>({});
  
  // Regen handler state
  const [triggerRegen, setTriggerRegen] = useState<(() => void) | null>(null);
  
  // Edit message popup state
  const [editPopupState, setEditPopupState] = useState<{
    isOpen: boolean;
    messageId: string;
    initialContent: string;
  }>({
    isOpen: false,
    messageId: '',
    initialContent: ''
  });

  // Backtrack confirmation popup state
  const [backtrackPopupState, setBacktrackPopupState] = useState<{
    isOpen: boolean;
    messageId: string;
  }>({
    isOpen: false,
    messageId: ''
  });

  // Image preview popup state
  const [imagePreviewState, setImagePreviewState] = useState<{
    isOpen: boolean;
    message: LiveMessage | null;
  }>({
    isOpen: false,
    message: null
  });

  // Memoize the regen request callback to prevent infinite re-renders
  const handleRegenRequest = useCallback((regenFn: () => void) => {
    setTriggerRegen(() => regenFn);
  }, []);
  
  // Resize handle state
  const [isDragging, setIsDragging] = useState(false);
  
  // Gallery popup state - use prop instead of local state
  const galleryPopupState = {
    isOpen: isGalleryOpen
  };
  
  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const { setChatStatus } = useChatStatus();

  // Centralized inactivity timer is managed automatically by globalChatState

  // Use the remove message hook
  const { removeMessage: deleteMessage } = useRemoveMessageFromChat({});

  // Use the edit message hook
  const { editMessage } = useEditMessage({});

  // Use the update active image hook
  const { updateActiveImage } = useUpdateActiveImage();

  // Use sticky scroll hook
  const { scrollToBottom, forceScrollToBottom, isSticky, setStickyTrue } = useStickyScroll({
    messagesEndRef,
    messages,
    showMessages,
    isImageGenerating: globalChatState.isImageGenerating,
  });

  // Register sticky scroll callback globally
  const { registerCallback, unregisterCallback } = useStickyScrollCallback(setStickyTrue);
  
  // Register the callback on mount and unregister on unmount
  React.useEffect(() => {
    registerCallback();
    return unregisterCallback;
  }, [registerCallback, unregisterCallback]);

  // Use message actions (now with global sticky scroll callback)
  const messageActions = useLiveMessageActions();

  // Use in-chat image generation hook
  const { 
    state: inChatImageGenState, 
    errorMessage: inChatImageGenError, 
    triggerInChatImageGen, 
    resetError: resetInChatImageGenError 
  } = useInChatImageGen({
    avatarId: character?.metadata.avatar_id || '',
    avatarName: character?.metadata.avatar_name || 'Assistant'
  });

  // State for message sending animation
  const [receivedFirstToken, setReceivedFirstToken] = useState(true);

  // Callback for handling in-chat image generation from ChatControls
  const handleInChatImageGen = useCallback((prompt: string) => {
    triggerInChatImageGen(prompt);
  }, [triggerInChatImageGen]);

  // Inactivity timer cleanup is handled automatically by globalChatState

  // Get user preferred name from UserAppData singleton - only after auth loading completes
  const [userData] = useUserAppData(
    !authLoading && isAuthenticated && userEmail ? userEmail : ''
  );

  // Gemini Live hook for voice chat volume data
  const geminiLive = useGeminiLive({
    avatarName: character?.metadata.avatar_name || 'Assistant',
    characterPrompt: character?.metadata.prompt || '',
    userPreferredName: userData.userPreferredName || 'User'
  });
  
  // Update local state when UserAppData changes
  useEffect(() => {
    if (userData.userPreferredName) {
      setUserPreferredName(userData.userPreferredName);
    }
  }, [userData.userPreferredName]);

  // Inactivity timer starts automatically when chatStatus becomes LIVE (handled by useGlobalChatState hook)

  // React to inactivity timeout state changes
  useEffect(() => {
    if (globalChatState.isInactivityTimeoutPopupOpen && room && room.state === 'connected') {
      console.log('[Inactivity] Disconnecting from LiveKit room due to timeout in TextChat');
      room.disconnect();
      setChatStatus(ChatStatus.INFO);
    }
  }, [globalChatState.isInactivityTimeoutPopupOpen, room, setChatStatus]);

  // Subscribe to LiveKit room events - only in non-preview mode
  // Note: Streaming responses are now handled directly through API calls in useSendText2API
  // No need for LiveKit event listeners for data reception

  // Reset image generation state when room changes
  useEffect(() => {
    if (room) {
      globalChatStateActions.setIsImageGenerating(false);
    }
  }, [room]);

  // Reset transcription index when leaving
  useEffect(() => {
    return () => {
      globalChatStateActions.setLastProcessedIndexForTranscriptionTracking(-1);
      globalChatStateActions.setStreamingMessageId('');
      globalChatStateActions.setIsImageGenerating(false);
    };
  }, []);

  // Filter messages for gallery and chat display
  const { galleryImages, chatMessages } = useMemo(() => {
    const galleryImages: LiveMessage[] = [];
    const chatMessages: LiveMessage[] = [];

    messages.forEach((msg) => {
      const isMsgAssistantImage = msg.role === 'assistant' && msg.imageUrl;
      
      if (isMsgAssistantImage) {
        galleryImages.push(msg);
        chatMessages.push(msg); // Also include in chat display
      } else {
        chatMessages.push(msg);
      }
    });

    return { galleryImages, chatMessages };
  }, [messages]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
      
      // Close message menus when clicking outside
      const target = event.target as Element;
      const isMessageMenuClick = target.closest('[data-message-menu]');
      if (!isMessageMenuClick) {
        setMessageMenuState({});
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const handleDeleteMessage = async (messageId: string) => {
    try {
      const lastMessageWithImage = messageActions.getLastMessageWithImage();
      const isDeletingLastMessageWithImage = lastMessageWithImage?.id === messageId;
      const currentActiveImage = globalChatState.activeImage?.imageUri || '';
      const needUpdate = isDeletingLastMessageWithImage && currentActiveImage === lastMessageWithImage?.imageUrl;

      await messageActions.removeMessage(messageId);
      // if the message is the last message with image, update the active image
      const updatedLastMessageWithImage = messageActions.getLastMessageWithImage();
      if (needUpdate && updatedLastMessageWithImage?.imageUrl) {
        globalChatStateActions.setActiveImage(updatedLastMessageWithImage?.imageUrl || '');
      }
      // Close the menu after deletion
      setMessageMenuState(prev => ({ ...prev, [messageId]: false }));
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleBacktrackMessage = (messageId: string) => {
    // Show confirmation popup instead of directly executing
    setBacktrackPopupState({
      isOpen: true,
      messageId: messageId
    });
    // Close the menu
    setMessageMenuState(prev => ({ ...prev, [messageId]: false }));
  };

  const handleSetDefaultImage = async (messageId: string) => {
    try {
      // Find the message by ID
      const message = messages.find(msg => msg.id === messageId);
      if (!message || !message.imageUrl) {
        console.error('Message or image not found');
        return;
      }

      // Update the active image (now includes automatic head detection and image swap)
      await globalChatStateActions.setActiveImage(message.imageUrl);
      console.log('Image set as default successfully');

      // Close the menu
      setMessageMenuState(prev => ({ ...prev, [messageId]: false }));

      // Head detection and image swap is now handled automatically in setActiveImage

    } catch (error) {
      console.error('Error setting image as default:', error);
    }
  };

  const handleBacktrackConfirm = async () => {
    const messageId = backtrackPopupState.messageId;
    try {
      // Find the index of the message to backtrack to
      const targetIndex = chatMessages.findIndex(msg => msg.id === messageId);
      
      if (targetIndex === -1) {
        console.error('Message not found for backtrack:', messageId);
        return;
      }
      
      // Get all messages that come after the target message and filter out image messages
      const messagesToDelete = chatMessages.slice(targetIndex + 1).filter(msg => !msg.imageUrl);

      // Delete each text message in reverse order to maintain proper indexing
      for (let i = messagesToDelete.length - 1; i >= 0; i--) {
        const messageToDelete = messagesToDelete[i];
        await messageActions.removeMessage(messageToDelete.id);
      }
      
      console.log(`Backtracked to message ${messageId}, deleted ${messagesToDelete.length} messages`);
    } catch (error) {
      console.error('Error during backtrack:', error);
    }
  };

  const handleBacktrackClose = () => {
    setBacktrackPopupState({
      isOpen: false,
      messageId: ''
    });
  };

  const handleRegenLastMessage = async (messageId: string) => {
    if (!triggerRegen) {
      console.error('Regen function not available');
      return;
    }

    try {
      // Delete the last assistant message
      await messageActions.removeMessage(messageId);
      
      // Trigger regen (send empty message)
      triggerRegen();
      
      // Close the menu after deletion
      setMessageMenuState(prev => ({ ...prev, [messageId]: false }));
    } catch (error) {
      console.error('Error regenerating message:', error);
    }
  };

  const handleEditMessage = (messageId: string, initialContent: string) => {
    setEditPopupState({
      isOpen: true,
      messageId,
      initialContent
    });
    // Close the menu
    setMessageMenuState(prev => ({ ...prev, [messageId]: false }));
  };

  const handleEditConfirm = async (newContent: string) => {
    try {
      await messageActions.editMessage(editPopupState.messageId, newContent);
    } catch (error) {
      console.error('Error editing message:', error);
    }
  };

  const handleEditClose = () => {
    setEditPopupState({
      isOpen: false,
      messageId: '',
      initialContent: ''
    });
  };

  // handleSetDefaultImage

  return (
    <div className="flex flex-col h-full">
      {/* Resizable Chat Messages Container */}
      <PanelGroup direction="vertical" className="flex-1 min-h-0">
        {/* Top spacer panel - adjustable */}
        <Panel defaultSize={0} minSize={0} maxSize={100}>
          <div className="h-full bg-transparent" />
        </Panel>

        {/* Resize Handle - Always Present, Conditionally Styled */}
        <PanelResizeHandle 
          className={`group relative h-2 transition-all duration-200 flex items-center justify-center ${
            showResizeHandle ? 'cursor-ns-resize' : 'cursor-default'
          }`}
          onDragging={showResizeHandle ? (isDraggingNow) => setIsDragging(isDraggingNow) : undefined}
        >
          {/* Laser Line - Full Width */}
          <div className={`w-full h-px transition-all duration-200 ${
            showResizeHandle ? 'bg-purple-500' : 'bg-transparent'
          }`}
            style={showResizeHandle ? {
              boxShadow: `
                0 -0px 2px #e879f9,
                0 -0px 4px #e879f9,
                0 -0px 8px #a855f7,
                0 -0px 16px #a855f7,
                0 -0px 32px #9333ea,
                0 -0px 64px #7c3aed
              `
            } : {}}
          ></div>
          
          {/* Two Line Affordances */}
          <div className="absolute flex flex-col space-y-1">
            <div className={`w-8 h-0.5 rounded-full transition-all duration-200 ${
              showResizeHandle ? 'bg-purple-400/60' : 'bg-transparent'
            }`}></div>
            <div className={`w-8 h-0.5 rounded-full transition-all duration-200 ${
              showResizeHandle ? 'bg-purple-400/60' : 'bg-transparent'
            }`}></div>
          </div>
        </PanelResizeHandle>

        {/* Combined Messages and Chat Control Panel */}
        <Panel defaultSize={100} minSize={0} maxSize={100}>
          <div className="h-full flex flex-col relative">
            {/* Messages Area - Flexible */}
            <div 
              className="flex-1 overflow-y-auto px-4 pt-4 dark-scrollbar"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(0, 0, 0, 0.7) transparent',
                WebkitMask: 'linear-gradient(to bottom, transparent 0%, black 5%, black 97%, transparent 100%)',
                mask: 'linear-gradient(to bottom, transparent 0%, black 5%, black 97%, transparent 100%)'
              }}
            >
              {/* Display safety warning */}
              {showMessages && (
                <div className="flex justify-center mb-4">
                  <div className="bg-gray-600/30 backdrop-blur-sm rounded-full px-4 py-1 border border-white/20">
                    <p className="text-white/70 text-xs text-center">
                      {t('allResponsesAIGenerated')}
                    </p>
                  </div>
                </div>
              )}

              {/* Display collapsible bio section */}
              {showMessages && character?.metadata.agent_bio && 
              character.metadata.agent_bio !== character.metadata.avatar_name && (
                <div className="mb-4">
                  <div className="bg-black/90 backdrop-blur-sm rounded-xl px-4 py-3 border border-gray-600/40">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p 
                          ref={bioTextRef}
                          className={`text-gray-200 text-sm leading-relaxed transition-all duration-300 ${
                            isBioExpanded ? '' : 'line-clamp-3'
                          }`}
                          style={{
                            display: isBioExpanded ? 'block' : '-webkit-box',
                            WebkitLineClamp: isBioExpanded ? 'none' : 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: isBioExpanded ? 'visible' : 'hidden'
                          }}
                        >
                          {character.metadata.agent_bio}
                        </p>
                      </div>
                      {needsCollapse && (
                        <button
                          onClick={() => setIsBioExpanded(!isBioExpanded)}
                          className="flex-shrink-0 p-1 rounded-full hover:bg-gray-700/30 transition-colors duration-200"
                          aria-label={isBioExpanded ? t('collapseBio') : t('expandBio')}
                        >
                          {isBioExpanded ? (
                            <ChevronUp className="w-4 h-4 text-white/70" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-white/70" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!showMessages ? (
                <div className="flex items-center justify-center h-full">
                </div>
              ) : (
                // Full chat history mode - always show all messages
                <div className="space-y-3.5">
                  {chatMessages.map((msg: LiveMessage, index: number) => {
                    const isDefaultMessage = msg.id.startsWith('default');
                    const isMsgFromUser = msg.role === 'user';
                    const isMsgAssistantImage = !isMsgFromUser && msg.role === 'assistant' && msg.imageUrl;
                    const isLastAssistantResponse = index === chatMessages.length - 1 
                                                    && msg.role === 'assistant'
                                                    && !isDefaultMessage;
                    
                    return (
                      <div key={msg.id} className={`flex ${isMsgFromUser ? 'justify-end' : 'justify-start'}`}>
                        <div 
                          className={`rounded-2xl break-words relative ${
                            isMsgFromUser 
                              ? 'text-black max-w-[85%] px-4 py-2' 
                              : isMsgAssistantImage
                              ? 'text-white max-w-[57%] px-2 py-2 bg-white/10 backdrop-blur-md'
                              : 'text-white max-w-[85%] px-4 py-2'
                          }`} 
                          style={{
                            backgroundColor: isMsgFromUser 
                              ? '#dedcdb' 
                              : isMsgAssistantImage
                              ? 'transparent'
                              : '#1e1e1e'
                          }}
                        >
                          {/* More Button - Only show in LIVE or TEXT mode */}
                          {!isDefaultMessage && !msg.isStreaming && (
                            <div className={`absolute top-2 ${isMsgFromUser ? 'right-1' : 'right-1'}`} data-message-menu>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const rect = e.currentTarget.getBoundingClientRect();
                                  setButtonPositions(prev => ({
                                    ...prev,
                                    [msg.id]: {
                                      top: rect.top,
                                      left: rect.left,
                                      right: rect.right,
                                      bottom: rect.bottom
                                    }
                                  }));
                                  setMessageMenuState(prev => ({ ...prev, [msg.id]: !prev[msg.id] }));
                                }}
                                className="flex items-center justify-center w-6 h-3 rounded-full transition-colors"
                              >
                                <MoreVertical className={`w-3 h-3 ${isMsgFromUser ? 'text-black/60' : 'text-white/60'}`} />
                              </button>

                              {/* Portal Dropdown Menu */}
                              <PortalDropdown
                                isOpen={messageMenuState[msg.id]}
                                position={buttonPositions[msg.id] || { top: 0, left: 0, right: 0, bottom: 0 }}
                                isMsgFromUser={isMsgFromUser}
                                onClose={() => setMessageMenuState(prev => ({ ...prev, [msg.id]: false }))}
                              >
                                {/* Only show regen button for last assistant messages */}
                                {!isMsgFromUser && isLastAssistantResponse && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRegenLastMessage(msg.id);
                                    }}
                                    className="w-full flex items-center justify-start px-4 py-2 text-white hover:bg-[#ffffff1a] hover:text-white/80 transition-colors rounded-xl"
                                  >
                                    <RefreshCw className="w-4 h-4 mr-2" />
                                    {t('redo')}
                                  </button>
                                )}
                                {/* Edit button for all non-default messages */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditMessage(msg.id, msg.content);
                                  }}
                                  className="w-full flex items-center justify-start px-4 py-2 text-white hover:bg-[#ffffff1a] hover:text-white/80 transition-colors rounded-xl"
                                >
                                  <Edit3 className="w-4 h-4 mr-2" />
                                  {t('edit')}
                                </button>
                                {/* Show backtrack button for all messages except the last one */}
                                {index < chatMessages.length - 1 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleBacktrackMessage(msg.id);
                                    }}
                                    className="w-full flex items-center justify-start px-4 py-2 text-white hover:bg-[#ffffff1a] hover:text-white/80 transition-colors rounded-xl"
                                  >
                                    <RotateCcw className="w-4 h-4 mr-2" />
                                    {t('backtrack')}
                                  </button>
                                )}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteMessage(msg.id);
                                  }}
                                  className="w-full flex items-center justify-start px-4 py-2 text-red-400 hover:bg-[#ffffff1a] hover:text-red-300 transition-colors rounded-xl"
                                >
                                  <Trash className="w-4 h-4 mr-2" />
                                  {t('delete')}
                                </button>
                              </PortalDropdown>
                            </div>
                          )}
                          
                          {/* Show message content */}
                          {isMsgAssistantImage ? (
                            // Image message display
                            <div className="relative">
                              {/* Image loading state */}
                              {msg.imageWithStatus?.loadingStatus === 'loading' && (
                                <div className="aspect-square flex items-center justify-center">
                                  <Loader2 className="w-6 h-6 animate-spin text-white/60" />
                                </div>
                              )}
                              
                              {/* Image display when loaded */}
                              {msg.imageWithStatus?.loadingStatus === 'loaded' && msg.imageWithStatus?.imageData && (
                                <div 
                                  className="cursor-pointer rounded-lg overflow-hidden"
                                  onClick={() => {
                                    setImagePreviewState({
                                      isOpen: true,
                                      message: msg
                                    });
                                  }}
                                >
                                  <AdvancedImage
                                    cldImg={msg.imageWithStatus.imageData}
                                    alt={msg.content || "Generated image"}
                                    className="w-full h-auto shadow-sm"
                                    style={{ 
                                      objectFit: 'cover',
                                      borderRadius: '8px',
                                      overflow: 'hidden'
                                    }}
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              
                              {/* Image failed to load */}
                              {msg.imageWithStatus?.loadingStatus === 'failed' && (
                                <div className="aspect-square flex items-center justify-center">
                                  <div className="text-white/40 text-xs">×</div>
                                </div>
                              )}
                            </div>
                          ) : (
                            // Text message display
                            <div className={`whitespace-pre-wrap leading-relaxed text-sm select-text cursor-text ${isMsgFromUser ? 'pr-2' : 'pr-2'}`}>
                              {isMsgFromUser ? msg.content : formatMessageContent(msg.content, msg.isStreaming, userPreferredName, avatar_name)}
                              {/* Loading animation for streaming messages */}
                              {!isMsgFromUser && msg.isStreaming && (
                                <span className="inline-block ml-1 w-0.5 h-4 bg-white animate-pulse"></span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* In-chat image generation status UI */}
                  {(inChatImageGenState === 'generating' || inChatImageGenState === 'applying') && (
                    <div className="flex justify-start">
                      <div
                        className="text-white px-2 py-2 w-[57%]"
                      >
                        <div className="relative">
                          <div className="cursor-pointer rounded-lg overflow-hidden">
                            {/* Show last image with same styling as regular image display */}
                            {galleryImages.length > 0 && galleryImages[galleryImages.length - 1].imageWithStatus?.imageData ? (
                              <AdvancedImage
                                cldImg={galleryImages[galleryImages.length - 1].imageWithStatus.imageData}
                                alt="Reference image"
                                className="w-full h-auto"
                                style={{
                                  objectFit: 'cover',
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                  filter: 'blur(4px) brightness(0.5)'
                                }}
                                loading="lazy"
                              />
                            ) : (
                              <div className="aspect-[9/16] w-full bg-[#2a2a2e]"></div>
                            )}
                            {/* Loading overlay */}
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                              <Loader2 className="w-8 h-8 animate-spin text-white" />
                              <p className="text-sm text-white font-medium">
                                {inChatImageGenState === 'generating' ? t('generating') : t('downloading')}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* In-chat image generation error UI */}
                  {inChatImageGenState === 'error' && inChatImageGenError && (
                    <div className="flex justify-start">
                      <div
                        className="text-white px-4 py-3 w-[85%] bg-[#3d2828] border border-red-400/30 relative"
                      >
                        <button
                          onClick={resetInChatImageGenError}
                          className="absolute top-2 right-2 text-white/60 hover:text-white transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                        <p className="text-sm text-red-400 pr-6">
                          {inChatImageGenError}
                        </p>
                        <p className="text-xs text-gray-400 mt-1 pr-6">
                          {t('noGemsDeducted')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Message sending animation */}
                  {!receivedFirstToken && (
                    <div className="flex justify-start">
                      <div className="rounded-2xl text-white px-4 py-3 max-w-[85%] bg-[#1e1e1e] relative">
                        <div className="flex items-center gap-2">
                          <div className="w-1.5 h-1.5 bg-white/60 rounded-full animate-pulse"></div>
                          <div className="w-1.5 h-1.5 bg-white/60 rounded-full animate-pulse [animation-delay:200ms]"></div>
                          <div className="w-1.5 h-1.5 bg-white/60 rounded-full animate-pulse [animation-delay:400ms]"></div>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Chat Control Area - Fixed at bottom */}
            <ChatControlWrapper>
              {/* Render shared LiveChatControl for active chat */}
              <LiveChatControl
                onRegenRequest={handleRegenRequest}
                onStartChat={onStartChat}
                onInChatImageGen={handleInChatImageGen}
                onReceivedFirstTokenChange={setReceivedFirstToken}
                showMessages={showMessages}
                onToggleShowMessages={() => setShowMessages(!showMessages)}
              />
            </ChatControlWrapper>
          </div>
        </Panel>
      </PanelGroup>

      {/* Gallery Popup */}
      <GalleryPopup
        isOpen={galleryPopupState.isOpen}
        onClose={onToggleGallery || (() => {})}
        images={galleryImages}
        onDeleteMessage={handleDeleteMessage}
        onSetDefaultImage={handleSetDefaultImage}
      />

      {/* Edit Message Popup */}
      <EditMessagePopup
        isOpen={editPopupState.isOpen}
        onClose={handleEditClose}
        onConfirm={handleEditConfirm}
        initialContent={editPopupState.initialContent}
        messageId={editPopupState.messageId}
      />

      {/* Backtrack Confirmation Popup */}
      <BacktrackConfirmPopup
        isOpen={backtrackPopupState.isOpen}
        onClose={handleBacktrackClose}
        onConfirm={handleBacktrackConfirm}
      />

      {/* Image Preview Popup */}
      <ImagePreviewPopup
        isOpen={imagePreviewState.isOpen}
        onClose={() => setImagePreviewState(prev => ({ ...prev, isOpen: false }))}
        message={imagePreviewState.message}
        onDeleteMessage={handleDeleteMessage}
        onSetDefaultImage={handleSetDefaultImage}
      />

     </div>
   );
}); 